import { BillingPlanDto, BillingSubscriptionDto } from '@shared/core/billing'
import { UserEventStatsDto } from '@shared/core/events'
import { GetProfileResponseDto } from '@shared/core/users'
import { UseQueryResult } from 'react-query'
import { CalendarDto } from 'src/types/calendar.dto'

export function resolveChurnSurveyActiveEventsCount(
  statsQuery: Pick<UseQueryResult<UserEventStatsDto>, 'isSuccess' | 'isError' | 'data'>,
): number | 'unknown' | null {
  if (!statsQuery.isSuccess && !statsQuery.isError) {
    return null
  }

  if (statsQuery.isError) {
    return 'unknown'
  }

  return statsQuery.data?.active_events_count ?? 0
}

export function resolveChurnSurveySelectedCalendarsCount(
  calendarsQuery: Pick<UseQueryResult<CalendarDto[]>, 'isSuccess' | 'isError' | 'data'>,
): number | 'unknown' | null {
  if (!calendarsQuery.isSuccess && !calendarsQuery.isError) {
    return null
  }

  if (calendarsQuery.isError) {
    return 'unknown'
  }

  return calendarsQuery.data?.filter((calendar) => calendar.selected).length ?? 0
}

export function resolveChurnSurveyPlanName(input: {
  subscriptionEnabled: boolean
  subscriptionQuery: Pick<UseQueryResult<BillingSubscriptionDto | null>, 'isSuccess' | 'isError' | 'data'>
  subscriptionPlansQuery: Pick<UseQueryResult<BillingPlanDto[]>, 'isError' | 'data'> | undefined
  currentPlanName: string | undefined
}): string {
  if (!input.subscriptionEnabled) {
    return input.currentPlanName ?? ''
  }

  if (input.subscriptionQuery.isError || input.subscriptionPlansQuery?.isError) {
    return 'unknown'
  }

  if (input.subscriptionQuery.isSuccess && input.subscriptionQuery.data == null) {
    return 'unknown'
  }

  if (input.subscriptionQuery.isSuccess && input.subscriptionQuery.data != null) {
    const planId = input.subscriptionQuery.data.plan_id
    const plans = input.subscriptionPlansQuery?.data

    if (planId == null || planId === 0) {
      return 'unknown'
    }

    if (Array.isArray(plans) && !plans.some((plan) => plan.paddle_id === planId)) {
      return 'unknown'
    }
  }

  return input.currentPlanName ?? ''
}

export function resolveChurnSurveySubscriptionStartDateIso(input: {
  subscriptionEnabled: boolean
  subscriptionQuery: Pick<UseQueryResult<BillingSubscriptionDto | null>, 'isSuccess' | 'isError' | 'data'>
  profileCreatedAt: Date | string
}): string {
  if (
    input.subscriptionEnabled &&
    (input.subscriptionQuery.isError || (input.subscriptionQuery.isSuccess && input.subscriptionQuery.data == null))
  ) {
    return 'unknown'
  }

  if (input.subscriptionQuery.data?.signup_date) {
    return toIso8601(input.subscriptionQuery.data.signup_date)
  }

  return toIso8601(input.profileCreatedAt)
}

export function isChurnSurveyEmbedReady(input: {
  profile: GetProfileResponseDto | undefined
  planLoading: boolean
  selectedCalendarsCount: number | 'unknown' | null
  activeEventsCount: number | 'unknown' | null
}): boolean {
  return (
    input.profile !== undefined &&
    !input.planLoading &&
    input.selectedCalendarsCount !== null &&
    input.activeEventsCount !== null
  )
}

function toIso8601(value: Date | string): string {
  return new Date(value).toISOString()
}
